import * as XLSX from "xlsx";
import { useEffect, useState } from "react";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useDebounce } from "../Debounce/useDebounce";
import { useToasts } from "react-toast-notifications";
import { useDispatch, useSelector } from "react-redux";

export const useServicabilitySettings = () => {
  const dispatch = useDispatch();

  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const { addToast } = useToasts();
  const [servicabilities, setServicabilities] = useState([]);
  const [loader, setLoader] = useState(false);

  //filters
  const [searchByState, setSearchByState] = useState(null);
  const [searchByCity, setSearchByCity] = useState(null);
  const [searchByCityCode, setSearchByCityCode] = useState(null);

  const debouncedSearchByState = useDebounce(searchByState, 300);
  const debouncedSearchByCity = useDebounce(searchByCity, 300);
  const debouncedSearchByCityCode = useDebounce(searchByCityCode, 300);

  const [selectedOption, setSelectedOption] = useState("state");
  const [searchValue, setSearchValue] = useState("");

  //Bulk edit
  const [selectedCities, setSelectedCities] = useState([]);
  const [bulkDeliveryCharge, setBulkDeliveryCharge] = useState(0);
  const [editingCity, setEditingCity] = useState(null);
  const [hoveredCity, setHoveredCity] = useState(null);
  const [showBulkEditPanel, setShowBulkEditPanel] = useState(false);

  const [pendingSave, setPendingSave] = useState(null);

  const [cityDeliveryCharges, setCityDeliveryCharges] = useState({});
  const [modalState, setModalState] = useState("servicebaility_summary");
  const [importedFile, setImportedFile] = useState(null);
  const [importedLocationsSummary, setImportedLocationsSummary] = useState({
    uploadedData: [],
    passed: 0,
    failed: 0,
    validLocations: [],
  });

  const [expandedStates, setExpandedStates] = useState({});
  const [selectAllState, setSelectAllState] = useState({});

  const handleStateToggle = (state, e) => {
    if (e.target.type === "checkbox") {
      return;
    }

    setExpandedStates((prev) => ({
      ...prev,
      [state]: !prev[state],
    }));
  };

  const handleSelectAllCities = (state, isServiceable) => {
    const cities = serviceableCities[state];
    let stateCities =
      showBulkEditPanel === "delivery"
        ? cities.filter((city) => city.is_serviceable)
        : cities;

    const allCityKeys = stateCities.map((area) => `${state}-${area.city}`);

    const allSelected = allCityKeys.every((cityKey) =>
      selectedCities.includes(cityKey)
    );

    if (allSelected) {
      setSelectedCities((prev) =>
        prev.filter((cityKey) => !allCityKeys.includes(cityKey))
      );
    } else {
      setSelectedCities((prev) => [
        ...prev,
        ...allCityKeys.filter((cityKey) => !prev.includes(cityKey)),
      ]);
    }

    setSelectAllState((prev) => ({
      ...prev,
      [state]: !allSelected,
    }));
  };
  const [isModalOpen, setIsModalOpen] = useState(false);

  const resetServicabilitySettings = () => {
    setSearchByState("");
    setSearchByCity("");
    setSearchByCityCode("");
    setSearchValue("");
    setSelectedOption("state");

    setSelectedCities([]);
    setBulkDeliveryCharge(0);
    setEditingCity(null);
    setHoveredCity(null);
    setShowBulkEditPanel(false);
    setCityDeliveryCharges({});
    setModalState("servicebaility_summary");
    setImportedFile(null);
    setImportedLocationsSummary({
      uploadedData: [],
      passed: 0,
      failed: 0,
      validLocations: [],
    });
    setExpandedStates({});
    setSelectAllState({});
  };

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
    resetServicabilitySettings();
  };

  const handleDropdownChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedOption(selectedValue);
    setSearchValue("");
    setSearchByState("");
    setSearchByCity("");
    setSearchByCityCode("");
  };

  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCityCode, setSelectedCityCode] = useState("");

  const handleStateChange = (e) => {
    const state = e.target.value;
    setSelectedState(state);
    setSelectedCity("");
    setSelectedCityCode("");

    setExpandedStates((prev) => ({
      ...prev,
      [state]: true,
    }));
  };

  const handleCityChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);

    if (selectedState) {
      setExpandedStates((prev) => ({
        ...prev,
        [selectedState]: true,
      }));
    }

    const selectedCityObj = serviceableCities[selectedState]?.find(
      (c) => c.city === city
    );
    setSelectedCityCode(selectedCityObj?.city_code || "");
  };

  const handleClearFilters = () => {
    setSelectedState("");
    setSelectedCity("");
    setSelectedCityCode("");
  };

  const getServicabilities = async () => {
    try {
      setLoader(true);

      let url = `${endpoint.GET_SERVICABILITIES}?`;

      if (debouncedSearchByState) {
        url += `&state=${debouncedSearchByState}`;
      }

      if (debouncedSearchByCity) {
        url += `&city=${debouncedSearchByCity}`;
      }
      if (debouncedSearchByCityCode) {
        url += `&city_code=${debouncedSearchByCityCode}`;
      }

      const result = await apiHandler({
        url: url,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (result.data.data) {
        setServicabilities(result.data.data);
      }
    } catch (error) {
      //console.error("Error fetching servicabilities:", error);
    }
  };

  useEffect(() => {
    getServicabilities();
  }, [
    debouncedSearchByState,
    debouncedSearchByCity,
    debouncedSearchByCityCode,
  ]);

  const groupByState = (data) => {
    return data.reduce((acc, area) => {
      if (!acc[area.states]) {
        acc[area.states] = [];
      }
      acc[area.states].push(area);
      return acc;
    }, {});
  };

  const groupedCities = (data) => {
    if (!Array.isArray(data)) {
 
      return { serviceableCities: {}, nonServiceableCities: {} };
    }

    return {
      serviceableCities: groupByState(data),
      nonServiceableCities: [],
    };
  };

  const { serviceableCities, nonServiceableCities } =
    groupedCities(servicabilities);

  const handleUpdateCityDeliveryCharge = (state, city, city_code) => {
    const deliveryChargeForCity = cityDeliveryCharges[`${state}-${city}`] || 0;

    const updatedCity = {
      city_code: city_code,
      delivery_charges: deliveryChargeForCity,
      is_serviceable: true,
    };

    handleCityUpdate([updatedCity], "delivery_charge", "bulk");
  };

  const handleCityUpdate = async (
    citiesToUpdate,
    updateType = "delivery_charge",
    type = "bulk"
  ) => {
    try {
      const result = await apiHandler({
        url: `${endpoint.UPDATE_SERVICABILITIES}`,
        method: "PATCH",
        authToken: authData,
        data: citiesToUpdate,
        dispatch,
        storeId,
      });

      setSelectAllState({});
      let updateMessage = "";

      if (type === "bulk") {
        updateMessage =
          updateType === "delivery_charge"
            ? "Delivery charges updated in selected cities."
            : `Serviceability ${
                citiesToUpdate[0].is_serviceable ? "enabled" : "disabled"
              } in selected cities.`;
      }

      if (result.data.success) {
        setServicabilities((prev) =>
          prev.map((area) => {
            const updatedCity = citiesToUpdate.find(
              (c) => c.city_code === area.city_code
            );
            return updatedCity ? { ...area, ...updatedCity } : area;
          })
        );

        if (type === "bulk" && citiesToUpdate.length > 1) {
          addToast(updateMessage, {
            appearance: "success",
            autoDismiss: true,
          });
        }
      } else {
        if (type === "bulk" && citiesToUpdate.length > 1) {
          addToast(result?.data?.error || "Error occurred", {
            appearance: "error",
            autoDismiss: true,
          });
        }
      }

      setSelectedCities([]);
      setEditingCity(null);
    } catch (error) {
      //console.error("Error updating cities:", error);
      if (type === "bulk" && citiesToUpdate.length > 1) {
        addToast("An error occurred while updating cities.", {
          appearance: "error",
          autoDismiss: true,
        });
      }
    }
  };

  const handleBulkEdit = (type) => {
    if (selectedCities.length === 0) {
      addToast("No valid cities selected for update.", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    if (type === "delivery") {
      if (!bulkDeliveryCharge) {
        addToast("Please enter a valid delivery charge.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const updatedCities = servicabilities
        .filter((area) =>
          selectedCities.includes(`${area.states}-${area.city}`)
        )
        .map((area) => ({
          city_code: area.city_code,
          delivery_charges: bulkDeliveryCharge,
          is_serviceable: true,
        }));

      setCityDeliveryCharges((prev) => ({
        ...prev,
        ...Object.fromEntries(
          selectedCities.map((key) => [key, bulkDeliveryCharge])
        ),
      }));

      handleCityUpdate(updatedCities, "delivery_charge", "bulk");
    }

    if (type === "serviceability") {
      const updatedCities = servicabilities
        .filter((area) =>
          selectedCities.includes(`${area.states}-${area.city}`)
        )
        .map((area) => ({
          city_code: area.city_code,
          is_serviceable: true,
          delivery_charges: area.delivery_charges ? area.delivery_charges : 0,
        }));

      handleCityUpdate(updatedCities, "serviceability", "bulk");
    }

    // Reset after update
    setShowBulkEditPanel(false);
    setBulkDeliveryCharge("");
    setSelectedCities([]);
  };

  const handleCheckboxChange = (state, city) => {
    const cityKey = `${state}-${city}`;

    setSelectedCities((prevSelected) =>
      prevSelected.includes(cityKey)
        ? prevSelected.filter((key) => key !== cityKey)
        : [...prevSelected, cityKey]
    );
  };

  const handleEdit = (state, city, charge, city_code) => {
    setEditingCity(`${state}-${city}`);
    setCityDeliveryCharges({
      ...cityDeliveryCharges,
      [`${state}-${city}`]: charge,
    });
  };

  const handleSaveEdit = (state, city, city_code) => {
    if (!cityDeliveryCharges[`${state}-${city}`]) {
      //console.warn("No changes detected for delivery charge.");
      return;
    }

    handleUpdateCityDeliveryCharge(state, city, city_code);
    setEditingCity(null);
  };

  function prepareCitiesforExport(action) {
    const result = [["State", "City", "City Code", "Delivery Charges"]];

    servicabilities
      .filter((item) =>
        action === "export_serviceable"
          ? item.is_serviceable
          : !item.is_serviceable
      )
      .forEach((item) => {
        result.push([
          item.states,
          item.city,
          item.city_code,
          action === "export_serviceable" ? item.delivery_charges : null,
        ]);
      });

    return result;
  }

  const handleExportImport = (action) => {
    const exportData = () => {
      const ws = XLSX.utils.aoa_to_sheet(prepareCitiesforExport(action));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        action === "export_serviceable"
          ? "Serviceable Locations"
          : "Non Serviceable Locations"
      );

      XLSX.writeFile(
        wb,
        action === "export_serviceable"
          ? "serviceable_locations.xlsx"
          : "non_serviceable_locations.xlsx"
      );
    };

    switch (action) {
      case "export_serviceable":
        exportData();
        break;
      case "export_non_serviceable":
        exportData();
        break;
      case "import_serviceable":
        setModalState("import_active");
        break;
    }
  };

  const SubmitImportedLocations = async () => {
    try {
      let tempValidLocations = importedLocationsSummary?.validLocations;

      if (
        !Array.isArray(tempValidLocations) ||
        tempValidLocations.length === 0
      ) {
        //console.error("Valid locations are empty.");
        return;
      }

      if (tempValidLocations.length > 1) {
        tempValidLocations.shift();
      }

      const formatedCities = tempValidLocations.map((location) => ({
        states: location[0],
        city: location[1],
        city_code: location[2],
        delivery_charges: location[3],
        is_serviceable: true,
      }));

      await handleCityUpdate(formatedCities);

      setIsModalOpen(!isModalOpen);
    } catch (error) {
      //console.error("Error updating cities:", error);
      addToast("An error occurred while updating cities.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  function adjustRowLength(row, headers) {
    const headerLength = headers.length;

    if (row.length < headerLength) {
      return [...row, ...Array(headerLength - row.length).fill(null)];
    } else if (row.length > headerLength) {
      return row.slice(0, headerLength);
    }

    return row;
  }
  const validateAndSummarizeImportedLocations = (data) => {
    if (!Array.isArray(data) || data.length < 2) {
    
      return {
        uploadedData: [
          ["State", "City", "City Code", "Delivery Charges", "Status"],
        ],
        validLocations: [],
        passed: 0,
        failed: 0,
        error: "No data rows found. Please upload a file with valid details.",
      };
    }

    const headers = data[0];
    const expectedHeaders = ["State", "City", "City Code", "Delivery Charges"];

    const isValidRow = (row) => {
      if (!row || row.length < 4) return false;

      if (row[3] === null) return false;

      const cityCode = row[2]?.trim();
      const deliveryCharges = String(row[3] || "").trim();

      return (
        typeof row[0] === "string" &&
        typeof row[1] === "string" &&
        /^std:\d+$/.test(cityCode) &&
        !isNaN(deliveryCharges) &&
        Number(deliveryCharges) >= 0
      );
    };

    const uploadedData = [];
    const validLocations = [];
    let passed = 0;
    let failed = 0;

    const isHeaderValid =
      JSON.stringify(headers) === JSON.stringify(expectedHeaders);
    if (!isHeaderValid) {
      return {
        uploadedData: [],
        validLocations: [],
        passed: 0,
        failed: 0,
        error: "Invalid file format. Please use the correct sample file.",
      };
    }

    uploadedData.push([...headers, "Status"]);

    for (let i = 1; i < data.length; i++) {
      let row = adjustRowLength(data[i], headers);
      if (row[3]) {
        row[3] = row[3] ? Number(String(row[3] || "").trim()) : null;
      }

      if (isValidRow(row)) {
        validLocations.push(row);
        uploadedData.push([...row, "passed"]);
        passed++;
      } else {
        uploadedData.push([...row, "failed"]);
        failed++;
      }
    }

    if (validLocations.length === 0) {
      //console.warn("No valid rows found. The file may only contain headers.");
    }

    return {
      uploadedData,
      validLocations,
      passed,
      failed,
      error:
        validLocations.length === 0
          ? "No valid data rows found. Please check your file."
          : null,
    };
  };
  const handleImport = (action, event) => {
    if (action === "file_upload") {
      const files = event.target.files;
      if (files && files[0]) {
        setImportedFile(files[0]);
      }
    } else if (action === "imported") {
      const reader = new FileReader();
      const rABS = !!reader.readAsBinaryString;
      reader.onload = (e) => {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });

        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

        setImportedLocationsSummary(
          validateAndSummarizeImportedLocations(data)
        );
      };
      if (rABS) reader.readAsBinaryString(importedFile);
      else reader.readAsArrayBuffer(importedFile);
      setModalState("import_summary");
    } else if (action === "validated") {
      setModalState("servicebaility_summary");
      SubmitImportedLocations();
    } else if (action === "back_to_upload") {
      setImportedFile(null);
      setModalState("import_active");
    }
  };

  const updateServiceability = async (city_code) => {
    let updatedCity = servicabilities.find(
      (area) => area.city_code === city_code
    );

    if (updatedCity) {
      setServicabilities((prev) =>
        prev.map((area) =>
          area.city_code === city_code
            ? {
                ...area,
                delivery_charges: 0,
                is_serviceable: !area.is_serviceable,
              }
            : area
        )
      );
      updatedCity = {
        ...updatedCity,
        delivery_charges: 0,
        is_serviceable: !updatedCity.is_serviceable,
      };

      handleCityUpdate([updatedCity], "serviceability", "single");
    }
  };

  const downloadSampleCSV = () => {
    if (!servicabilities || servicabilities.length === 0) {
      //console.error("No serviceability data available.");
      return;
    }

    const headers = ["State", "City", "City Code", "Delivery Charges"];

    const rows = servicabilities.map((location) => [
      location.states,
      location.city,
      location.city_code,
      location.delivery_charges,
    ]);

    const csvData = [headers, ...rows];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "serviceability_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    loader,
    servicabilities,
    handleDropdownChange,
    // handleSearchChange,
    selectedOption,
    searchValue,
    handleClearFilters,
    handleModalToggle,
    isModalOpen,
    serviceableCities,
    nonServiceableCities,

    handleBulkEdit,
    bulkDeliveryCharge,
    setBulkDeliveryCharge,
    selectedCities,

    cityDeliveryCharges,
    setCityDeliveryCharges,

    handleCheckboxChange,
    handleEdit,
    handleSaveEdit,
    editingCity,
    hoveredCity,
    setHoveredCity,

    handleExportImport,
    modalState,
    handleImport,
    importedFile,
    importedLocationsSummary,
    updateServiceability,
    downloadSampleCSV,
    expandedStates,
    handleStateToggle,
    handleSelectAllCities,
    selectAllState,
    showBulkEditPanel,
    setShowBulkEditPanel,
    pendingSave,
    setPendingSave,
    handleUpdateCityDeliveryCharge,
    setEditingCity,
    selectedState,
    selectedCity,
    selectedCityCode,
    handleStateChange,
    handleCityChange,
  };
};
